import {
    DUAL_MOBILITY_HEAD_SIZE,
    DUAL_MOBILITY_INNER_DIAMETER_SIZE,
    findBearing,
    findCatalogLiner,
    findHead,
    getCatalogComponent,
} from '@/domain/catalog/catalog';
import type { PlannerState } from '@/views/3d-planner/store/plannerState';
import type { Url } from '@/domain/types';
import { assert } from '@/util/assert';
import type { CatalogHead } from '@/domain/catalog/head';
import type { CatalogStem } from '@/domain/catalog/stem';
import { LinerType } from '@/domain/catalog/liner';

export function computeCupCollisionSurfaceUrl(state: PlannerState): Url | null {
    return state.template
        ? state.catalog?.cups.get(state.template.cupUrl)?.collisionMask ?? null
        : null;
}

export function computeLinerUrl(state: PlannerState): Url {
    assert(state.catalog && state.template);
    const cupUrl = state.template.cupUrl;
    const catalogCup = state.catalog.cups.get(state.template.cupUrl);
    if (!catalogCup) {
        throw Error(`Invalid cup url: ${cupUrl}`);
    }
    if (state.template.dualMobility) {
        return findCatalogLiner(
            state.catalog,
            catalogCup.linerSize,
            DUAL_MOBILITY_HEAD_SIZE,
            LinerType.DUAL_MOBILITY,
        ).self;
    } else {
        return findCatalogLiner(state.catalog, catalogCup.linerSize, catalogCup.recommendedHeadSize)
            .self;
    }
}

export function computeBearingUrl(state: PlannerState): Url | null {
    assert(state.template && state.catalog);
    if (state.template.dualMobility) {
        const liner = state.catalog.liners.get(state.template.linerUrl);
        assert(liner);
        return findBearing(state.catalog, liner.size, DUAL_MOBILITY_INNER_DIAMETER_SIZE).self;
    } else {
        return null;
    }
}

export function computeHeadUrl(state: PlannerState): Url {
    return computeHead(state).self;
}

/** The catalog stem currently selected in the template. */
export function computeStem(state: PlannerState): CatalogStem {
    assert(state.template && state.catalog);
    return getCatalogComponent(state.catalog.stems, state.template.stemUrl);
}

export function computeHead(state: PlannerState): CatalogHead {
    assert(state.template && state.catalog);
    const originalHead = state.catalog.heads.get(state.template.headUrl);
    assert(originalHead);
    if (state.template.dualMobility) {
        return findHead(
            state.catalog,
            originalHead.system,
            DUAL_MOBILITY_HEAD_SIZE,
            originalHead.offset,
        );
    } else {
        const catalogCup = state.catalog.cups.get(state.template.cupUrl);
        assert(catalogCup);
        return findHead(
            state.catalog,
            originalHead.system,
            catalogCup.recommendedHeadSize,
            originalHead.offset,
        );
    }
}
